import { useEffect, useRef, useState, type JSX } from 'react';
import { type PopperPlacementType } from '@mui/base';
import { Icon } from '@doxyme/icons';
import { useTheme } from '../../../theme';
import {
  DropdownCellOptionType,
  Dropdown,
  type DropdownProps,
  type Option,
  type OptionGroup,
  isOptionGroup,
} from '../dropdown';
import { getButtonVariant, useFilterStyles } from './filter.styles';
import { BodyS } from '../../../typography';

const getSelectedOptionLabel = (
  options: Option[] | OptionGroup[],
  selected: string | null,
): string | undefined => {
  if (isOptionGroup(options)) {
    return options
      .flatMap((group) => group.options)
      .find((option) => option.value === selected)?.label;
  }

  return options.find((option) => option.value === selected)?.label;
};

export type FilterProps = {
  dropdownMaxHeight?: DropdownProps['maxHeight'];
  type?: DropdownCellOptionType;
  icon?: JSX.Element;
  options: Option[] | OptionGroup[];
  selected: string | null;
  onOptionClick: (value: string) => void;
  disabled?: boolean;
  dropdownPlacement?: PopperPlacementType;
  filterButtonHasLabel?: boolean;
};

export const Filter = ({
  dropdownMaxHeight,
  type = DropdownCellOptionType.Default,
  icon,
  options,
  selected,
  onOptionClick,
  disabled = false,
  dropdownPlacement = 'bottom-start',
  filterButtonHasLabel = true,
}: FilterProps): JSX.Element => {
  const theme = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const styles = useFilterStyles({ isOpen });
  const buttonVariant = getButtonVariant(theme);

  const selectedOptionLabel = getSelectedOptionLabel(options, selected);

  const toggleOpen = (): void => {
    setIsOpen(!isOpen);
  };

  const closeDropdown = (): void => setIsOpen(false);

  useEffect(() => {
    if (disabled) {
      closeDropdown();
    }
  }, [disabled]);

  return (
    <>
      <button
        type='button'
        data-testid='filter-toggle-button'
        ref={buttonRef}
        onClick={disabled ? undefined : toggleOpen}
        disabled={disabled}
        css={{
          ...styles.filter,
          ...buttonVariant.base,
          ...(isOpen && buttonVariant.active),
          ...(disabled && buttonVariant.disabled),
        }}
      >
        {icon}
        {filterButtonHasLabel && (
          <BodyS tag='span' css={styles.label}>
            {selectedOptionLabel}
          </BodyS>
        )}
        <Icon.ChevronDown css={styles.chevron} size={16} />
      </button>
      <Dropdown
        maxHeight={dropdownMaxHeight}
        type={type}
        isOpen={isOpen}
        onClose={closeDropdown}
        anchorElement={buttonRef.current}
        options={options}
        selected={selected}
        onOptionClick={(value) => {
          onOptionClick(value);
          closeDropdown();
        }}
        offset={Number.parseInt(theme.spacing(1))}
        placement={dropdownPlacement}
      />
    </>
  );
};
